import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

interface Location {
  latitude: number;
  longitude: number;
  timestamp: number; // When this location was fetched
}

interface LocationContextType {
  location: Location | null;
  isLoading: boolean;
  error: string | null;
  refreshLocation: () => Promise<void>;
  clearLocation: () => void;
  isLocationStale: () => boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const LOCATION_STORAGE_KEY = 'user_location';
const LOCATION_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes (configurable)

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCheckedCache, setHasCheckedCache] = useState<boolean>(false);
  const hasAutoFetchedRef = useRef<boolean>(false);

  // Load cached location from localStorage on mount
  useEffect(() => {
    const loadCachedLocation = () => {
      try {
        const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (cached) {
          const parsed: Location = JSON.parse(cached);
          const age = Date.now() - parsed.timestamp;
          
          // If location is fresh enough, use it
          if (age < LOCATION_MAX_AGE_MS) {
            console.log(`📍 Using cached location (${Math.round(age / 1000 / 60)} minutes old)`);
            setLocation(parsed);
          } else {
            console.log(`📍 Cached location is stale (${Math.round(age / 1000 / 60)} minutes old), will refresh`);
            // Don't clear it yet - still better than nothing
            setLocation(parsed);
          }
        }
      } catch (err) {
        console.error('Failed to load cached location:', err);
        localStorage.removeItem(LOCATION_STORAGE_KEY);
      } finally {
        // Mark that we've checked the cache
        setHasCheckedCache(true);
      }
    };

    loadCachedLocation();
  }, []);

  // Check if current location is stale (older than max age)
  const isLocationStale = useCallback((): boolean => {
    if (!location) return true;
    const age = Date.now() - location.timestamp;
    return age >= LOCATION_MAX_AGE_MS;
  }, [location]);

  // Fetch fresh location from browser
  const fetchLocation = useCallback(async (): Promise<void> => {
    if (!navigator.geolocation) {
      setError('location_not_supported');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation: Location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: Date.now()
          };

          // Save to state and localStorage
          setLocation(newLocation);
          localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
          
          console.log('📍 Fresh location obtained and cached');
          setIsLoading(false);
          setError(null);
          resolve();
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = 'location_denied';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'location_denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'location_unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'location_timeout';
              break;
          }
          
          setError(errorMessage);
          setIsLoading(false);
          reject(error);
        },
        {
          enableHighAccuracy: false, // Don't need GPS precision, saves battery
          timeout: 10000, // 10 second timeout
          maximumAge: 5 * 60 * 1000 // Accept 5-minute-old browser cache
        }
      );
    });
  }, []);

  // Public method to refresh location (can be called manually by user)
  const refreshLocation = useCallback(async (): Promise<void> => {
    console.log('📍 Manually refreshing location...');
    return fetchLocation();
  }, [fetchLocation]);

  // Auto-fetch location AFTER checking cache, if we don't have a fresh one
  useEffect(() => {
    // Wait until we've checked the cache before deciding to fetch
    if (!hasCheckedCache || hasAutoFetchedRef.current) return;
    
    const shouldFetch = !location || isLocationStale();
    
    if (shouldFetch && !isLoading) {
      console.log('📍 Auto-fetching location (no cache or stale)');
      hasAutoFetchedRef.current = true; // Prevent multiple auto-fetches
      fetchLocation().catch(() => {
        // Error already handled in fetchLocation
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCheckedCache]); // Only run once after cache check completes

  // Clear location (useful for testing or privacy)
  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    console.log('📍 Location cache cleared');
  }, []);

  const value: LocationContextType = {
    location,
    isLoading,
    error,
    refreshLocation,
    clearLocation,
    isLocationStale
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Hook to use location context
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

// Helper to get location coordinates (commonly used)
export const useLocationCoords = (): {
  latitude?: number;
  longitude?: number;
  isLoading: boolean;
  error: string | null;
} => {
  const { location, isLoading, error } = useLocation();
  
  return {
    latitude: location?.latitude,
    longitude: location?.longitude,
    isLoading,
    error
  };
};
